import { DefaultTheme } from 'styled-components';

/**
 * Ajusta um hex clareando (amount > 0) ou escurecendo (amount < 0).
 * Usado para derivar variações da cor de marca dinâmica do tenant.
 */
export function shade(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return hex;
  const num = parseInt(h, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  const t = amount < 0 ? 0 : 255;
  const p = Math.abs(amount);
  r = Math.round((t - r) * p + r);
  g = Math.round((t - g) * p + g);
  b = Math.round((t - b) * p + b);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

const neutral = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
};

const HEADING_FONT =
  "'Poppins', 'Inter', system-ui, -apple-system, sans-serif";
const BODY_FONT =
  "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

/**
 * Constrói o tema v2 a partir das cores de marca do tenant.
 * Fonte única da verdade — o ThemeContext usa esta função.
 */
export function buildTheme(
  primary: string = '#2563eb',
  secondary: string = '#1e40af'
): DefaultTheme {
  return {
    colors: {
      // Marca (dinâmica por tenant)
      primary,
      secondary,
      accent: primary,
      onPrimary: '#ffffff',
      primarySoft: shade(primary, 0.92),
      primaryDark: shade(primary, -0.12),

      // Superfícies / neutros
      background: '#ffffff',
      surface: neutral[50],
      muted: neutral[100],
      mutedForeground: neutral[500],
      card: '#ffffff',
      cardForeground: neutral[900],
      foreground: neutral[900],
      border: neutral[200],
      ring: primary,
      neutral,

      // Texto
      text: {
        primary: neutral[900],
        secondary: neutral[500],
        light: '#ffffff',
      },

      // Semânticas
      success: '#16a34a',
      warning: '#d97706',
      error: '#dc2626',
    },
    spacing: {
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem',
      xxl: '3rem',
      xxxl: '4rem',
    },
    borderRadius: {
      sm: '0.5rem',
      md: '0.75rem',
      lg: '1rem',
      xl: '1.5rem',
      full: '9999px',
    },
    shadows: {
      sm: '0 1px 2px 0 rgba(15, 23, 42, 0.06)',
      md: '0 2px 8px -1px rgba(15, 23, 42, 0.08)',
      lg: '0 10px 25px -5px rgba(15, 23, 42, 0.10)',
      xl: '0 24px 48px -12px rgba(15, 23, 42, 0.18)',
      card: '0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
      hover: '0 12px 28px -8px rgba(15, 23, 42, 0.18)',
      overlay: '0 24px 64px -16px rgba(15, 23, 42, 0.35)',
    },
    fonts: {
      primary: BODY_FONT,
      secondary: BODY_FONT,
      heading: HEADING_FONT,
    },
    typography: {
      h1: {
        fontSize: 'clamp(2.25rem, 4vw, 3.25rem)',
        lineHeight: '1.1',
        fontWeight: '700',
        letterSpacing: '-0.02em',
        marginBottom: '1.5rem',
      },
      h2: {
        fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
        lineHeight: '1.2',
        fontWeight: '700',
        letterSpacing: '-0.02em',
        marginBottom: '1rem',
      },
      h3: {
        fontSize: '1.5rem',
        lineHeight: '1.3',
        fontWeight: '600',
        letterSpacing: '-0.01em',
        marginBottom: '0.75rem',
      },
      h4: {
        fontSize: '1.25rem',
        lineHeight: '1.4',
        fontWeight: '600',
        marginBottom: '0.5rem',
      },
      h5: {
        fontSize: '1.125rem',
        lineHeight: '1.4',
        fontWeight: '600',
        marginBottom: '0.5rem',
      },
      h6: {
        fontSize: '1rem',
        lineHeight: '1.4',
        fontWeight: '600',
        marginBottom: '0.5rem',
      },
      body: {
        fontSize: '1rem',
        lineHeight: '1.6',
        fontWeight: '400',
      },
      bodyLarge: {
        fontSize: '1.125rem',
        lineHeight: '1.7',
        fontWeight: '400',
      },
      bodySmall: {
        fontSize: '0.875rem',
        lineHeight: '1.5',
        fontWeight: '400',
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: '1.4',
        fontWeight: '500',
      },
      button: {
        fontSize: '0.95rem',
        lineHeight: '1.5',
        fontWeight: '600',
        letterSpacing: '0',
      },
    },
  };
}

export const theme: DefaultTheme = buildTheme();
